<template>
    <div v-prefix v-clicked-outside="() => { focus_search(false) }" class="#tag flex flex-column relative">
        <ul v-show="value.length" class="reset">
            <li v-prefix v-for="tag in value" :key="tag.id" class="#tag-search__list-item border-light-a">
                <icon-wrapper icon="sf-minus-minimal">
                    <button 
                        v-prefix
                        @click="remove_tag_from_filter(tag.id)" 
                        :aria-label="`Remove ${tag.name} from search filters`" 
                        class="#tag-search__list-item-btn reset flex flex-align-center w-100 text-left" 
                        type="button"
                    >#{{ tag.name }}</button>
                </icon-wrapper>
            </li>
        </ul>

        <icon-wrapper 
            :class="{ 
                'is-focused': search_focused, }" 
                :icon="search_focused ? 'sf-search-2' : 'sf-plus-minimal'" 
                size="1.5rem"
            >
            <input 
                @focus="focus_search" 
                v-model="search_keyword" 
                :placeholder="tag_search_placeholder" 
                type="text" 
                class="sidearm-galleries-input" 
                ref="search" 
                :tabindex="$parent.value ? '0' : '-1'"
            />
        </icon-wrapper>

        <div v-prefix :class="{ 'is-toggled': search_focused }" class="#tag-search__dropdown bg-white">
            <ul v-prefix class="#tag-search__dropdown-list reset">
                <li v-prefix v-show="filtered_search_results.length" class="#tag-search__dropdown-list-item --separator type-small bg-lighter text-on-light pa-1">Search Results</li>
                
                <li v-prefix v-for="tag in filtered_search_results" :key="tag.id" class="#tag-search__dropdown-list-item">
                    <button 
                        v-prefix 
                        @click="add_tag_to_filter(tag)" 
                        :aria-label="`Add ${tag.name} to search filters`" 
                        class="#tag-search__dropdown-list-btn reset flex flex-align-center w-100 text-left" 
                        type="button"
                    >#{{ tag.name }}</button>
                </li>

                <li v-prefix v-if="recommended_tags.length" class="#tag-search__dropdown-list-item --separator type-small bg-lighter text-on-light pa-1">Recommended Tags</li>

                <li v-prefix v-for="tag in filtered_recommended_tags" :key="tag.id" class="#tag-search__dropdown-list-item">
                    <button 
                        v-prefix 
                        @click="add_tag_to_filter(tag)" 
                        :aria-label="`Add ${tag.name} to search filters`" 
                        class="#tag-search__dropdown-list-btn reset flex flex-align-center w-100 text-left" 
                        type="button"
                    >#{{ tag.name }}</button>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import api from '@/api'
    import { sort_by } from '@/helpers'
    import debounce from 'lodash-es/debounce'

    export default {
        props: {
            value: {
                type: Array,
                required: true,
                default: () => ([])
            }
        },

        data: () => ({
            tag_popover_open: false,

            search_focused: false,

            search_keyword: '',

            search_results: [],

            recommended_tags: [],
        }),

        computed: {
            filtered_search_results() {
                return this.search_results.filter(player => this.value.every(p => p.id !== player.id)).slice(0,5);
            },

            filtered_recommended_tags() {
                return this.recommended_tags.filter(tag => this.value.every(t => t.id !== tag.id)).slice(0,3);
            },

            tag_search_placeholder() {
                if (this.search_focused) {
                    return 'Type to search tags...';
                }
                else if (this.value.length) {
                    return `${this.value.length} tags selected`;
                }
                else {
                    return 'Select tags';
                }
            },
        },

        watch: {
            search_keyword(newVal, oldVal) {
                if (newVal) {
                    if (newVal.length >= 2) {
                        this.search_tags();
                    }
                }
                else {
                    this.search_results = [];
                }
            }
        },

        methods: {
            toggle_tag_popover(value) {
                if (value !== undefined) {
                    this.tag_popover_open = !!value;
                }
                else {
                    this.tag_popover_open = !this.tag_popover_open;
                }

                if (this.tag_popover_open === true) {
                    this.$refs.search.focus();
                }
            },

            focus_search(value = true) {
                if (value === false) {
                    this.search_results = [];
                    this.search_keyword = '';
                }

                this.search_focused = value;
            },

            search_tags: debounce(function() {
                api.search_tags(this.search_keyword)
                    .then(tags => {
                        if (this.search_keyword) {
                            this.search_results = tags;
                        }
                    });
            }, 600),

            add_tag_to_filter(tag) {
                this.$nextTick(() => {
                    this.search_focused = true;
                });

                let updated_tags = this.value.slice();

                updated_tags.push(tag);

                this.$emit('input', updated_tags);
            },

            remove_tag_from_filter(tag_id) {
                let updated_tags = this.value.slice();

                updated_tags = updated_tags.filter(tag => tag.id !== tag_id);

                this.$emit('input', updated_tags);
            },
        },

        created() {
            api.search_tags()
                .then(tags => {
                    this.recommended_tags = tags.sort(sort_by('count')).reverse().slice(0,9);
                })
        }
    }
</script>

<style lang="scss" scoped>
    .#{$prefix}__tag-search {
        &__list-item { 
            margin-bottom: -1px;
        }

        &__list-item, &__dropdown-list-item {
            transition: .35s;

            &:not(.--separator):hover {
                background-color: $color-primary;
                color: $text-on-primary;
            }
        }

        &__list-item-btn {
            padding: $spacer !important;
        }

        &__dropdown {
            @include hide();
            box-shadow: 0px 1px $spacer rgba($color-black, 0.08);

            &.is-toggled {
                @include unhide();

                position: absolute;
                top: 100%;
                left: 0;
                width: 100%;
            }
        }

        &__dropdown-list-item {
            &:not(:last-child) {
                border-bottom: 1px solid $stroke-on-light;
            }
        }

        &__dropdown-list-btn {
            padding: $spacer !important;
        }
    }
</style>